import { Button, Flex, FormControl, Heading } from "@chakra-ui/react";
import { Section } from "./_Section";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatCurrency, formatCurrencyToApi, showToast } from "../../utils";
import { useSeller } from "../../hooks/useSeller";
import { Input } from "../../components/Forms/Input";
import { StatusText } from "../../components/Forms/StatusText";
import { Checkbox } from "../../components/Forms/Checkbox";
import { useParams } from "react-router-dom";
import { useState } from "react";

const editInvoiceSchema = yup.object().shape({
  withholdingTax: yup.boolean(),
  pisValue: yup.string().required("Preenchimento obrigatório!"),
  pisFreeValue: yup.string().required("Preenchimento obrigatório!"),
  cofinsValue: yup.string().required("Preenchimento obrigatório!"),
  cofinsFreeValue: yup.string().required("Preenchimento obrigatório!"),
  inssValue: yup.string().required("Preenchimento obrigatório!"),
  inssFreeValue: yup.string().required("Preenchimento obrigatório!"),
  csllValue: yup.string().required("Preenchimento obrigatório!"),
  csllFreeValue: yup.string().required("Preenchimento obrigatório!"),
  irValue: yup.string().required("Preenchimento obrigatório!"),
  irFreeValue: yup.string().required("Preenchimento obrigatório!"),
});

interface InvoiceSectionProps {
  invoice: {
    active: boolean;
    withholdingTax: boolean;
    pisValue: number;
    pisFreeValue: number;
    cofinsValue: number;
    cofinsFreeValue: number;
    inssValue: number;
    inssFreeValue: number;
    csllValue: number;
    csllFreeValue: number;
    irValue: number;
    irFreeValue: number;
    invoiceTest: {
      status: string;
      uuid: string;
      errorReason: string;
    }
  },
}

export interface InvoiceSectionData {
  withholdingTax: boolean;
  pisValue: number;
  pisFreeValue: number;
  cofinsValue: number;
  cofinsFreeValue: number;
  inssValue: number;
  inssFreeValue: number;
  csllValue: number;
  csllFreeValue: number;
  irValue: number;
  irFreeValue: number;
}

export function InvoiceSection({ invoice }: InvoiceSectionProps) {
  const { patchInvoice, sellerActiveInvoice, activeLoading, testLoading, sellerInvoiceTest } = useSeller();
  let { id } = useParams();
  const [editSection, setEditSection] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editInvoiceSchema),
  });

  const onSubmit = handleSubmit(async ({
    withholdingTax,
    pisValue,
    cofinsValue,
    inssValue,
    csllValue,
    irValue,
    pisFreeValue,
    cofinsFreeValue,
    inssFreeValue,
    csllFreeValue,
    irFreeValue
  }: InvoiceSectionData) => {
    try {
      const invoiceData = {
        withholdingTax: withholdingTax ?? false,
        pisValue: Number(pisValue),
        cofinsValue: Number(cofinsValue),
        inssValue: Number(inssValue),
        csllValue: Number(csllValue),
        irValue: Number(irValue),
        pisFreeValue: formatCurrencyToApi(pisFreeValue),
        cofinsFreeValue: formatCurrencyToApi(cofinsFreeValue),
        inssFreeValue: formatCurrencyToApi(inssFreeValue),
        csllFreeValue: formatCurrencyToApi(csllFreeValue),
        irFreeValue: formatCurrencyToApi(irFreeValue),
      }
      await patchInvoice(invoiceData, id);
      setEditSection("");
      showToast({ message: "Informacoes editadas com sucesso", type: "success" });
    } catch (error: any) {
      setEditSection("");
      error?.response?.data?.errors?.map((error:any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
      return
    }
  });

  return (
    <Section
      sectionTitle="Nota fiscal"
      isEdit={editSection === "invoice"}
      onClickEditButton={() => {
        setEditSection("invoice");
      }}
      onClickConfirmButton={onSubmit}
      buttons={
        [
          <Button
            borderRadius="2px"
            border="1px solid #D9D9D9"
            bgColor="white"
            p="0 0.5rem"
            fontSize="14px"
            onClick={() => sellerActiveInvoice(id, invoice?.active)}
            isDisabled={activeLoading}
            isLoading={activeLoading}
          >
            {invoice?.active ? "Desativar NF" : "Ativar NF"}
          </Button>,

          <Button
            borderRadius="2px"
            border="1px solid #D9D9D9"
            bgColor="white"
            p="0 0.5rem"
            fontSize="14px"
            onClick={() => sellerInvoiceTest(id)}
            isDisabled={testLoading}
            isLoading={testLoading}
            visibility={invoice?.invoiceTest?.status === "Em processamento" ? "hidden" : "visible"}
          >
            Testar NF
          </Button>,
        ]}
    >
      <Flex gap={2} direction="column">
        <Flex>
          <FormControl maxWidth="280px">
            <StatusText
              label="Status da última nota fiscal"
              name="status"
              p="0"
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              error={invoice?.invoiceTest?.status === "Falhou"}
              errorMessage={invoice?.invoiceTest?.errorReason}
            >
              {invoice.invoiceTest?.status}
            </StatusText>
          </FormControl>
          <FormControl maxWidth="280px">
            <Checkbox
              label="Imposto retido na fonte"
              name="withholdingTax"
              defaultChecked={invoice.withholdingTax}
              p="0"
              register={{ ...register("withholdingTax") }}
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              isDisabled={editSection !== "invoice"}
            />
          </FormControl>
        </Flex>
        <Flex gap={2} alignItems="center">
          <Flex
            border="1px solid"
            p="16px"
            borderColor="gray.200"
            borderRadius="4px"
          >
            <FormControl
              maxWidth="280px"
            >
              <Heading fontSize="16px">IR</Heading>
              <Input
                label="Taxa"
                defaultValue={invoice?.irValue}
                name="irValue"
                type="number"
                mask="percentage"
                register={{ ...register("irValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.irValue}
              />
              <Input
                label="Valor mínimo não isento"
                defaultValue={formatCurrency({ value: invoice?.irFreeValue })}
                name="irFreeValue"
                mask="currency"
                register={{ ...register("irFreeValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.irFreeValue}
              />
            </FormControl>
          </Flex>
          <Flex
            border="1px solid"
            p="16px"
            borderColor="gray.200"
            borderRadius="4px"
          >
            <FormControl maxWidth="280px">
              <Heading fontSize="16px">Cofins</Heading>
              <Input
                label="Taxa"
                defaultValue={invoice?.cofinsValue}
                name="cofinsValue"
                register={{ ...register("cofinsValue") }}
                p="0"
                type="number"
                mask="percentage"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.cofinsValue}

              />
              <Input
                label="Valor mínimo não isento"
                defaultValue={formatCurrency({ value: invoice?.cofinsFreeValue })}
                name="cofinsFreeValue"
                register={{ ...register("cofinsFreeValue") }}
                p="0"
                mask="currency"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.cofinsFreeValue}
              />
            </FormControl>
          </Flex>
          <Flex
            border="1px solid"
            p="16px"
            borderColor="gray.200"
            borderRadius="4px"
          >
            <FormControl maxWidth="280px">
              <Heading fontSize="16px">INSS</Heading>
              <Input
                label="Taxa"
                defaultValue={invoice?.inssValue}
                name="inssValue"
                type="number"
                mask="percentage"
                register={{ ...register("inssValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.inssValue}
              />
              <Input
                label="Valor mínimo não isento"
                defaultValue={formatCurrency({ value: invoice?.inssFreeValue })}
                name="inssFreeValue"
                mask="currency"
                register={{ ...register("inssFreeValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.inssFreeValue}

              />
            </FormControl>
          </Flex>
        </Flex>
        <Flex gap={2}>
          <Flex
            border="1px solid"
            p="16px"
            borderColor="gray.200"
            borderRadius="4px"
          >
            <FormControl maxWidth="280px">
              <Heading fontSize="16px">PIS</Heading>
              <Input
                label="Taxa"
                defaultValue={invoice?.pisValue}
                name="pisValue"
                type="number"
                mask="percentage"
                register={{ ...register("pisValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.pisValue}
              />
              <Input
                label="Valor mínimo não isento"
                defaultValue={formatCurrency({ value: invoice?.pisFreeValue })}
                name="pisFreeValue"
                mask="currency"
                register={{ ...register("pisFreeValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.pisFreeValue}
              />
            </FormControl>
          </Flex>
          <Flex
            border="1px solid"
            p="16px"
            borderColor="gray.200"
            borderRadius="4px"
          >
            <FormControl maxWidth="280px">
              <Heading fontSize="16px">CSLL</Heading>
              <Input
                label="Taxa"
                defaultValue={invoice?.csllValue}
                type="number"
                name="csllValue"
                mask="percentage"
                register={{ ...register("csllValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.csllValue}
              />
              <Input
                label="Valor mínimo não isento"
                defaultValue={formatCurrency({ value: invoice?.csllFreeValue })}
                name="csllFreeValue"
                mask="currency"
                register={{ ...register("csllFreeValue") }}
                p="0"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "invoice"}
                errors={errors?.csllFreeValue}
              />
            </FormControl>
          </Flex>
        </Flex>
      </Flex>
    </Section>
  );
}